import React from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { PersonSearch } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import SearchControls from './SearchControls';
import PatientSearchResults from './PatientSearchResults';

type StepSearchPatientsProps = {
    query: string;
    setQuery: (q: string) => void;
    sortBy: string;
    setSortBy: (s: string) => void;
    onSearch: () => void;
    isLoading: boolean;
    isFetching: boolean;
    isSearchPending: boolean;
    error: unknown;
    filteredPatients: DICOMPatientPresentation[];
    expandedPatients: Record<string, boolean>;
    onToggleExpanded: (id: string) => void;
    onSelectPatient: (patient: DICOMPatientPresentation) => void;
    availableTargets: string[];
    selectedTargetName: string;
    onTargetChange: (target: string) => void;
};

const StepSearchPatients = React.memo(
    ({
        query,
        setQuery,
        sortBy,
        setSortBy,
        onSearch,
        isLoading,
        isFetching,
        isSearchPending,
        error,
        filteredPatients,
        expandedPatients,
        onToggleExpanded,
        onSelectPatient,
        availableTargets,
        selectedTargetName,
        onTargetChange,
    }: StepSearchPatientsProps) => {
        const hasSearchTerm = query.trim().length > 0;

        return (
            <Box
                display="flex"
                flexDirection="column"
                height="100%"
                overflow="hidden"
            >
                {availableTargets.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            whiteSpace="nowrap"
                        >
                            <FormattedMessage id="selectDicomTarget" />:
                        </Typography>
                        <Select
                            size="small"
                            value={selectedTargetName}
                            onChange={(e) => onTargetChange(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            {availableTargets.map((target) => (
                                <MenuItem key={target} value={target}>
                                    {target}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}
                <SearchControls
                    query={query}
                    setQuery={setQuery}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    onSearch={onSearch}
                />

                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                    minHeight={0}
                    mt={1}
                >
                    {!hasSearchTerm && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            gap={1.5}
                            py={6}
                        >
                            <PersonSearch
                                sx={{ fontSize: 64, color: 'text.disabled' }}
                            />
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                textAlign="center"
                            >
                                <FormattedMessage id="searchForPatientPrompt" />
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                textAlign="center"
                            >
                                <FormattedMessage id="searchForPatientHint" />
                            </Typography>
                        </Box>
                    )}

                    {hasSearchTerm && (
                        <>
                            {isSearchPending || isLoading || isFetching ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="200px"
                                >
                                    <Typography>
                                        <FormattedMessage id="loadingPatients" />
                                    </Typography>
                                </Box>
                            ) : error ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="200px"
                                >
                                    <Typography color="error">
                                        <FormattedMessage id="errorLoadingPatients" />
                                    </Typography>
                                </Box>
                            ) : filteredPatients.length > 0 ? (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        my={1}
                                        flexShrink={0}
                                    >
                                        <FormattedMessage id="searchResults" />{' '}
                                        ({filteredPatients.length})
                                    </Typography>
                                    <Box
                                        flex={1}
                                        overflow="hidden"
                                        minHeight={0}
                                    >
                                        <PatientSearchResults
                                            patients={filteredPatients}
                                            expandedPatients={expandedPatients}
                                            onToggleExpanded={onToggleExpanded}
                                            onImportImages={onSelectPatient}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="200px"
                                >
                                    <Typography color="text.secondary">
                                        <FormattedMessage id="noResultsFound" />
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        );
    }
);
StepSearchPatients.displayName = 'StepSearchPatients';

export default StepSearchPatients;
